import { useNavigate } from "react-router-dom";
import SupportFaqSection from "components/SupportFaqSection";
import { PAGE_STORAGE_KEY } from "lib/theme";

export default function Support() {
  const navigate = useNavigate();

  const scrollToFaqs = () => {
    document.getElementById("support-faqs")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main className="flex-1 p-4 md:p-10 overflow-y-auto flex flex-col items-center">
      <div className="w-full max-w-[900px]">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0 mb-[20px] sm:mb-[30px]">
          <div className="text-2xl font-bold text-[var(--dash-text-strong)] flex items-center gap-2.5">
            <i className="fas fa-headset text-[var(--theme-accent)]"></i> Support
          </div>
          <button
            type="button"
            onClick={() => {
              localStorage.setItem(PAGE_STORAGE_KEY, "front");
              navigate("/");
            }}
            className="text-sm px-[15px] py-2 border border-[var(--dash-border)] rounded-md text-[var(--dash-text)] hover:border-[var(--dash-text-strong)] hover:text-[var(--dash-text-strong)] transition-all flex items-center gap-2 cursor-pointer self-start sm:self-auto"
          >
            <i className="fas fa-arrow-left"></i> Back to Dashboard
          </button>
        </div>

        <section className="flex min-h-dvh flex-col">
          <div className="flex flex-1 flex-col justify-center py-8 md:py-12">
            <div className="mb-10 text-center md:mb-12">
              <h1 className="mb-[10px] text-[32px] font-bold text-[var(--dash-text-strong)]">
                How can we help you?
              </h1>
              <p className="text-base text-[var(--dash-text)]">
                Choose a channel below to get in touch with our team.
              </p>
            </div>

            <div className="mb-8 flex flex-col flex-wrap items-center justify-center gap-[20px] sm:mb-10 sm:flex-row sm:gap-[30px]">
              <a
                href="https://wa.me/919399434740"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex h-[320px] w-[280px] flex-col items-center justify-center rounded-[16px] border border-[var(--dash-border)] bg-[var(--dash-surface)] px-[30px] py-[50px] text-center no-underline transition-all duration-300 hover:-translate-y-[5px] hover:border-[var(--theme-accent)] hover:bg-[var(--dash-surface-hover)]"
              >
                <div className="mb-[25px] flex h-20 w-20 items-center justify-center rounded-full bg-[color-mix(in_srgb,var(--theme-accent)_15%,transparent)] text-[35px] text-[var(--theme-accent)] transition-all duration-300 group-hover:bg-[var(--theme-accent)] group-hover:text-white">
                  <i className="fab fa-whatsapp"></i>
                </div>
                <div className="mb-2 text-[22px] font-bold text-[var(--dash-text-strong)]">
                  WhatsApp Us
                </div>
                <div className="text-[15px] text-[var(--dash-text)]">Get instant answers via chat.</div>
              </a>

              <a
                href="https://mail.google.com/mail/?view=cm&fs=1&tf=1&to=support@wa-contact-extractor.com"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex h-[320px] w-[280px] flex-col items-center justify-center rounded-[16px] border border-[var(--dash-border)] bg-[var(--dash-surface)] px-[30px] py-[50px] text-center no-underline transition-all duration-300 hover:-translate-y-[5px] hover:border-[#58a6ff] hover:bg-[var(--dash-surface-hover)]"
              >
                <div className="mb-[25px] flex h-20 w-20 items-center justify-center rounded-full bg-[rgba(88,166,255,0.15)] text-[35px] text-[#58a6ff] transition-all duration-300 group-hover:bg-[#58a6ff] group-hover:text-white">
                  <i className="fas fa-envelope"></i>
                </div>
                <div className="mb-2 text-[22px] font-bold text-[var(--dash-text-strong)]">
                  Email Us
                </div>
                <div className="text-[15px] text-[var(--dash-text)]">Send us a detailed query.</div>
              </a>

              <button
                type="button"
                onClick={scrollToFaqs}
                className="group flex h-[320px] w-[280px] cursor-pointer flex-col items-center justify-center rounded-[16px] border border-[var(--dash-border)] bg-[var(--dash-surface)] px-[30px] py-[50px] text-center transition-all duration-300 hover:-translate-y-[5px] hover:border-[#FFD700] hover:bg-[var(--dash-surface-hover)]"
              >
                <div className="mb-[25px] flex h-20 w-20 items-center justify-center rounded-full bg-[rgba(255,215,0,0.15)] text-[35px] text-[#FFD700] transition-all duration-300 group-hover:bg-[#FFD700] group-hover:text-[var(--dash-sidebar)]">
                  <i className="fas fa-circle-question"></i>
                </div>
                <div className="mb-2 text-[22px] font-bold text-[var(--dash-text-strong)]">FAQs</div>
                <div className="text-[15px] text-[var(--dash-text)]">Find quick answers below.</div>
              </button>
            </div>

            <div className="text-center">
              <div className="inline-block rounded-[12px] border border-dashed border-[var(--dash-border)] bg-[rgba(255,255,255,0.03)] px-10 py-5">
                <div className="mb-[5px] flex items-center justify-center gap-2 text-sm font-bold text-[#FFD700]">
                  <i className="far fa-clock"></i> Business Hours
                </div>
                <div className="text-[13px] text-[#888]">
                  Monday - Saturday: 10:00 AM - 7:00 PM (IST)
                </div>
              </div>
            </div>
          </div>
        </section>

        <section
          id="support-faqs"
          className="mt-16 border-t border-[var(--dash-border)] pt-14 md:mt-20 md:pt-16 scroll-mt-6"
        >
          <h2 className="mb-2 text-center text-2xl font-bold text-[var(--dash-text-strong)] md:text-3xl">
            Frequently asked questions
          </h2>
          <p className="mx-auto mb-10 max-w-2xl text-center text-sm text-[var(--dash-text)] md:text-base">
            Browse by topic. Click a question to show the answer.
          </p>
          <SupportFaqSection />
        </section>
      </div>
    </main>
  );
}
